<script setup lang="ts">
/**
 * Where a table's figures come from, and the one thing to do about it.
 *
 * Shown on a table that was read from CSRD. It says which entity, reporting
 * and disclosure, when the figures were read and what state CSRD's own
 * workflow had them in, whether they still agree with CSRD, and holds the
 * button that reads them again.
 *
 * It also says why the cells will not take typing. A locked table with no
 * explanation looks broken.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import { Callout } from '@/components/ui'
import type { TableSection } from '@/domain/section'
import { readingDate } from '@/features/csrdLink/format'
import { useCsrdLinksStore } from '@/stores/csrdLinks'
import { useReportStore } from '@/stores/report'

const { t, locale } = useI18n()

const props = defineProps<{ section: TableSection }>()

const links = useCsrdLinksStore()
const report = useReportStore()

const link = computed(() => props.section.csrd)
const state = computed(() => links.stateOf(props.section.id))

const busy = computed(() => state.value.phase === 'checking' || state.value.phase === 'refreshing')

/** Whether the figures agree, in a sentence. */
const stateText = computed(() => {
  switch (state.value.phase) {
    case 'checking':
      return t('csrdLink.state.checking')
    case 'refreshing':
      return t('csrdLink.state.refreshing')
    case 'stale':
      return state.value.changed > 0
        ? t('csrdLink.state.stale', { count: state.value.changed }, state.value.changed)
        : t('csrdLink.state.layout')
    case 'failed':
      return ''
    default:
      return t('csrdLink.state.current')
  }
})

/** What the last refresh did, kept on screen until the next one. */
const resultText = computed(() => {
  const last = state.value.last
  if (!last) return ''
  if (last.changed === 0 && !last.reshaped) return t('csrdLink.result.nothing')
  const date = readingDate(last.at, locale.value)
  const moved = t('csrdLink.result.updated', { count: last.changed, date }, last.changed)
  return last.reshaped ? `${moved} ${t('csrdLink.result.reshaped')}` : moved
})

const read = computed(() => readingDate(link.value?.pulledAt ?? '', locale.value))
</script>

<template>
  <section v-if="link" class="link" :aria-label="t('csrdLink.title')">
    <header class="link__head">
      <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
        <path
          d="M8 2.2c2.6 1.7 3.9 3.6 3.9 5.8 0 2.4-1.7 4.3-3.9 5.8-2.2-1.5-3.9-3.4-3.9-5.8 0-2.2 1.3-4.1 3.9-5.8z"
          fill="none"
          stroke="currentColor"
          stroke-width="1.2"
          stroke-linejoin="round"
        />
      </svg>
      <span class="link__title">{{ t('csrdLink.title') }}</span>
    </header>

    <dl class="link__facts">
      <div>
        <dt>{{ t('csrdLink.facts.entity') }}</dt>
        <dd>{{ link.entityCode }}</dd>
      </div>
      <div>
        <dt>{{ t('csrdLink.facts.reporting') }}</dt>
        <dd>{{ link.reportingCode }}</dd>
      </div>
      <div>
        <dt>{{ t('csrdLink.facts.disclosure') }}</dt>
        <dd>{{ link.disclosure }}</dd>
      </div>
      <div v-if="read">
        <dt>{{ t('csrdLink.facts.read') }}</dt>
        <dd>{{ read }}</dd>
      </div>
      <div>
        <dt>{{ t('csrdLink.facts.status') }}</dt>
        <dd :title="t('csrdLink.statusHint')">
          {{
            t('csrdLink.status', {
              collected: link.collected,
              controlled: link.controlled,
              audited: link.audited,
            })
          }}
        </dd>
      </div>
    </dl>

    <p
      v-if="stateText"
      class="link__state"
      :class="{ 'link__state--stale': state.phase === 'stale' }"
      role="status"
    >
      {{ stateText }}
    </p>
    <p v-if="state.problem" class="link__problem" role="alert">{{ state.problem }}</p>
    <button
      v-if="state.blocked"
      type="button"
      class="b-btn b-btn--primary link__refresh"
      @click="links.moveCsrdTo(state.blocked)"
    >
      {{ t('csrdLink.banner.switch', { code: state.blocked }) }}
    </button>

    <button
      type="button"
      class="b-btn b-btn--outline link__refresh"
      :disabled="busy || !report.editable"
      @click="links.refresh(section.id)"
    >
      {{ t('csrdLink.refresh') }}
    </button>

    <p v-if="resultText" class="link__result" role="status">
      {{ resultText }} {{ state.last && state.last.changed > 0 ? t('csrdLink.result.undo') : '' }}
    </p>

    <Callout tone="neutral" size="sm">{{ t('csrdLink.locked') }}</Callout>
  </section>
</template>

<style scoped>
.link {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  padding: var(--sp-5);
  border: 1px solid var(--hairline);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.link__head {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  color: var(--accent);
  font-size: var(--text-sm);
  font-weight: 600;
}

.link__facts {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  margin: 0;
  font-size: var(--text-xs);
}

.link__facts div {
  display: flex;
  gap: var(--sp-4);
  justify-content: space-between;
}

.link__facts dt {
  flex: none;
  color: var(--ink-faint);
}

.link__facts dd {
  margin: 0;
  min-width: 0;
  color: var(--ink);
  text-align: right;
  overflow-wrap: anywhere;
}

.link__state {
  margin: 0;
  color: var(--ink-soft);
  font-size: var(--text-xs);
}

.link__state--stale {
  color: var(--warn);
  font-weight: 600;
}

.link__problem {
  margin: 0;
  color: var(--danger);
  font-size: var(--text-xs);
}

.link__refresh {
  width: 100%;
}

.link__result {
  margin: 0;
  color: var(--ink-soft);
  font-size: var(--text-xs);
}
</style>
